import { useSyncExternalStore, useEffect, useRef } from 'react';
import type {
  ServerDoc, ServerLiveStats, MachineTelemetry,
  HubToBrowserMessage, BrowserToHubMessage, ConsoleLine, NodeCommand,
  BrowserBulkActionMsg, BulkServerStatus,
} from '@panel/shared';

export interface BulkServerEntry {
  Server_ID: number;
  Server_Name: string;
  Server_Machine: string;
  status: BulkServerStatus;
  progress: number;
  message?: string;
  error?: string;
  consoleLines: string[];
}

export interface BulkRun {
  bulk_id: string;
  command: BrowserBulkActionMsg['command'];
  target: BrowserBulkActionMsg['target'];
  startedAt: number;
  servers: Map<number, BulkServerEntry>;
  totals: { total: number; succeeded: number; failed: number; skipped: number; cancelled: number } | null;
  done: boolean;
}

export interface NotificationItem {
  id: string;
  ts: number;
  level: 'info' | 'success' | 'warn' | 'error';
  title: string;
  message?: string;
  /** Optional link to navigate to when clicked. */
  link?: string;
  read: boolean;
}

interface State {
  connected: boolean;
  /** True once the initial snapshot has arrived. Until then, "missing" entities
   *  in servers/machines mean "still loading", not "doesn't exist". */
  hasSnapshot: boolean;
  user: { discord_id: string; discord_username: string; role: string } | null;
  servers: Map<number, ServerDoc>;
  liveStats: Map<number, ServerLiveStats>;
  machines: Map<string, MachineTelemetry>;
  nodeStatus: Map<string, { online: boolean; last_seen: number }>;
  consoleByServer: Map<number, ConsoleLine[]>;
  // Per-action progress and result
  actions: Map<string, { progress: number; message?: string; status: 'pending' | 'ok' | 'error'; error?: string; result?: unknown; consoleLines: string[] }>;
  /** Most recent bulk run (only one at a time for the UI). */
  bulk: BulkRun | null;
  /** Persistent in-session notification feed. Newest first. Capped at 100. */
  notifications: NotificationItem[];
  /** Toast queue — recent notifications shown briefly top-right. */
  toasts: NotificationItem[];
}

const state: State = {
  connected: false,
  hasSnapshot: false,
  user: null,
  servers: new Map(),
  liveStats: new Map(),
  machines: new Map(),
  nodeStatus: new Map(),
  consoleByServer: new Map(),
  actions: new Map(),
  bulk: null,
  notifications: [],
  toasts: [],
};

// -------------------------------------------------------------------
// Notifications
// -------------------------------------------------------------------

const NOTIF_CAP = 100;
const TOAST_TTL_MS = 6000;

function pushNotification(n: Omit<NotificationItem, 'id' | 'ts' | 'read'>): void {
  const item: NotificationItem = {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    ts: Date.now(),
    read: false,
    ...n,
  };
  state.notifications = [item, ...state.notifications].slice(0, NOTIF_CAP);
  state.toasts = [...state.toasts, item];
  emit();
  setTimeout(() => {
    state.toasts = state.toasts.filter((t) => t.id !== item.id);
    emit();
  }, TOAST_TTL_MS);
}

export function dismissToast(id: string): void {
  state.toasts = state.toasts.filter((t) => t.id !== id);
  emit();
}

export function markAllNotificationsRead(): void {
  if (!state.notifications.some((n) => !n.read)) return;
  state.notifications = state.notifications.map((n) => n.read ? n : { ...n, read: true });
  emit();
}

export function clearNotifications(): void {
  if (state.notifications.length === 0) return;
  state.notifications = [];
  emit();
}

export function markNotificationRead(id: string): void {
  const idx = state.notifications.findIndex((n) => n.id === id);
  if (idx < 0 || state.notifications[idx].read) return;
  const next = state.notifications.slice();
  next[idx] = { ...next[idx], read: true };
  state.notifications = next;
  emit();
}

// -------------------------------------------------------------------
// Critical-threshold alerts
// -------------------------------------------------------------------

const ALERT_COOLDOWN_MS = 10 * 60 * 1000; // don't fire the same alert more than every 10m
const FPS_LOW = 10;
const FPS_BREACH_COUNT = 2; // consecutive sub-FPS_LOW samples before alerting
const DISK_CRITICAL = 90;
const MEM_CRITICAL  = 92;

const lastFiredAt = new Map<string, number>();
const lowFpsStreak = new Map<number, number>();

/** Fires a notification at most once per cooldown window per key. */
function alertOnce(key: string, build: () => Omit<NotificationItem, 'id' | 'ts' | 'read'>): void {
  const now = Date.now();
  const last = lastFiredAt.get(key) ?? 0;
  if (now - last < ALERT_COOLDOWN_MS) return;
  lastFiredAt.set(key, now);
  pushNotification(build());
}

function checkMachineAlerts(tel: import('@panel/shared').MachineTelemetry): void {
  // Memory
  if (tel.memory.total_bytes > 0) {
    const memPct = (tel.memory.used_bytes / tel.memory.total_bytes) * 100;
    if (memPct >= MEM_CRITICAL) {
      alertOnce(`mem:${tel.machine_name}`, () => ({
        level: 'error',
        title: `${tel.machine_name} memory critical`,
        message: `RAM usage at ${memPct.toFixed(0)}% — investigate before swap kicks in.`,
        link: `/machines/${encodeURIComponent(tel.machine_name)}`,
      }));
    }
  }

  // Disks
  for (const d of tel.disks) {
    if (d.total_bytes <= 0) continue;
    const diskPct = (d.used_bytes / d.total_bytes) * 100;
    if (diskPct >= DISK_CRITICAL) {
      alertOnce(`disk:${tel.machine_name}:${d.mount}`, () => ({
        level: 'error',
        title: `${tel.machine_name} disk ${d.mount} ${diskPct >= 99 ? 'is full' : 'nearly full'}`,
        message: `${diskPct.toFixed(0)}% used — backups and updates will fail soon.`,
        link: `/machines/${encodeURIComponent(tel.machine_name)}`,
      }));
    }
  }
}

function checkServerAlerts(stats: import('@panel/shared').ServerLiveStats): void {
  const fps = stats.rcon?.Framerate;
  if (stats.status !== 'online' || fps == null) {
    lowFpsStreak.delete(stats.Server_ID);
    return;
  }
  if (fps < FPS_LOW) {
    const next = (lowFpsStreak.get(stats.Server_ID) ?? 0) + 1;
    lowFpsStreak.set(stats.Server_ID, next);
    if (next >= FPS_BREACH_COUNT) {
      const server = state.servers.get(stats.Server_ID);
      alertOnce(`fps:${stats.Server_ID}`, () => ({
        level: 'warn',
        title: `${server?.Server_Name ?? `#${stats.Server_ID}`} server FPS low`,
        message: `FPS at ${fps} for ${next} consecutive polls — server is struggling.`,
        link: `/servers/${stats.Server_ID}`,
      }));
    }
  } else {
    lowFpsStreak.delete(stats.Server_ID);
  }
}

const listeners = new Set<() => void>();
function emit() { for (const l of listeners) l(); }
function subscribe(l: () => void) { listeners.add(l); return () => { listeners.delete(l); }; }
function getSnapshot() { return state; }

export function useStore<T>(selector: (s: State) => T): T {
  // useSyncExternalStore needs getSnapshot() to return the same reference between calls
  // when the selected value hasn't changed. We cache the last selector result and only
  // recompute when the selector returns something structurally different.
  const cache = useRef<{ result: T; hasValue: boolean }>({ result: undefined as T, hasValue: false });

  const getCachedSnapshot = () => {
    const fresh = selector(getSnapshot());
    if (!cache.current.hasValue) {
      cache.current = { result: fresh, hasValue: true };
      return fresh;
    }
    // If the selector returned the same reference, return the cached value.
    // If it returned a new reference but structurally equal, also return cached
    // (this handles `?? []` returning a fresh empty array each call).
    if (Object.is(fresh, cache.current.result)) return cache.current.result;
    if (shallowEqual(fresh, cache.current.result)) return cache.current.result;
    cache.current.result = fresh;
    return fresh;
  };

  return useSyncExternalStore(subscribe, getCachedSnapshot, getCachedSnapshot);
}

function shallowEqual(a: unknown, b: unknown): boolean {
  if (Object.is(a, b)) return true;
  if (typeof a !== 'object' || a === null || typeof b !== 'object' || b === null) return false;
  // Maps
  if (a instanceof Map && b instanceof Map) {
    if (a.size !== b.size) return false;
    for (const [k, v] of a) if (!Object.is(b.get(k), v)) return false;
    return true;
  }
  // Arrays
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false;
    for (let i = 0; i < a.length; i++) if (!Object.is(a[i], b[i])) return false;
    return true;
  }
  // Plain objects
  const ka = Object.keys(a as Record<string, unknown>);
  const kb = Object.keys(b as Record<string, unknown>);
  if (ka.length !== kb.length) return false;
  for (const k of ka) {
    if (!Object.is((a as Record<string, unknown>)[k], (b as Record<string, unknown>)[k])) return false;
  }
  return true;
}

// -------------------------------------------------------------------
// WebSocket connection
// -------------------------------------------------------------------

let ws: WebSocket | null = null;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let reconnectAttempts = 0;

function send(msg: BrowserToHubMessage): void {
  if (ws?.readyState === WebSocket.OPEN) ws.send(JSON.stringify(msg));
}

function connect(): void {
  const url = `${location.protocol === 'https:' ? 'wss:' : 'ws:'}//${location.host}/ws/browser`;
  ws = new WebSocket(url);
  ws.onopen = () => {
    reconnectAttempts = 0;
    state.connected = true;
    emit();
    send({ type: 'subscribe', topics: ['all'] });
  };
  ws.onmessage = (evt) => handleMessage(JSON.parse(evt.data) as HubToBrowserMessage);
  ws.onclose = () => {
    state.connected = false;
    emit();
    scheduleReconnect();
  };
  ws.onerror = () => { /* close will fire */ };
}

function scheduleReconnect(): void {
  reconnectAttempts++;
  const base = Math.min(15_000, 500 * Math.pow(2, Math.min(6, reconnectAttempts)));
  const delay = base + Math.floor(Math.random() * 500);
  if (reconnectTimer) clearTimeout(reconnectTimer);
  reconnectTimer = setTimeout(() => connect(), delay);
}

function handleMessage(m: HubToBrowserMessage): void {
  switch (m.type) {
    case 'hello':
      state.user = m.user;
      break;
    case 'snapshot':
      state.servers = new Map(m.servers.map((s) => [s.Server_ID, s]));
      state.liveStats = new Map(Object.entries(m.live).map(([k, v]) => [parseInt(k, 10), v]));
      state.machines = new Map(Object.entries(m.telemetry));
      state.nodeStatus = new Map(Object.entries(m.node_status));
      state.hasSnapshot = true;
      break;
    case 'machine_telemetry':
      state.machines.set(m.payload.machine_name, m.payload);
      state.machines = new Map(state.machines); // new ref so React picks it up
      checkMachineAlerts(m.payload);
      break;
    case 'server_stats': {
      const prev = state.liveStats.get(m.payload.Server_ID);
      state.liveStats.set(m.payload.Server_ID, m.payload);
      state.liveStats = new Map(state.liveStats);
      checkServerAlerts(m.payload);
      // Server went online → offline while its node is still online: most
      // likely a crash. Don't fire on planned restarts (status: 'restarting').
      if (
        prev && prev.status === 'online' && m.payload.status === 'offline'
        && state.nodeStatus.get(state.servers.get(m.payload.Server_ID)?.Server_Machine ?? '')?.online
      ) {
        const server = state.servers.get(m.payload.Server_ID);
        pushNotification({
          level: 'error',
          title: `${server?.Server_Name ?? `#${m.payload.Server_ID}`} went offline`,
          message: 'Possible crash — check the Console tab.',
          link: `/servers/${m.payload.Server_ID}`,
        });
      }
      break;
    }
    case 'node_status': {
      const prev = state.nodeStatus.get(m.machine_name);
      state.nodeStatus.set(m.machine_name, { online: m.online, last_seen: m.last_seen });
      state.nodeStatus = new Map(state.nodeStatus);
      // Only notify on transitions, not on the first observation.
      if (prev && prev.online !== m.online) {
        pushNotification({
          level: m.online ? 'success' : 'warn',
          title: `Machine ${m.machine_name} ${m.online ? 'reconnected' : 'went offline'}`,
          link: `/machines/${encodeURIComponent(m.machine_name)}`,
        });
      }
      break;
    }
    case 'console': {
      const list = state.consoleByServer.get(m.payload.Server_ID) ?? [];
      const next = [...list, m.payload].slice(-500);
      state.consoleByServer.set(m.payload.Server_ID, next);
      state.consoleByServer = new Map(state.consoleByServer);
      break;
    }
    case 'data_changed': {
      if (m.entity === 'server') {
        if (m.op === 'deleted') state.servers.delete(m.id as number);
        else if (m.data) state.servers.set(m.id as number, m.data as ServerDoc);
        state.servers = new Map(state.servers);
      }
      break;
    }
    case 'action_ack':
      state.actions.set(m.action_id, { progress: 0, status: 'pending', consoleLines: [] });
      state.actions = new Map(state.actions);
      break;
    case 'action_progress': {
      const cur = state.actions.get(m.action_id) ?? { progress: 0, status: 'pending' as const, consoleLines: [] };
      cur.progress = m.progress;
      cur.message = m.message;
      if (m.console) cur.consoleLines = [...cur.consoleLines, m.console].slice(-500);
      state.actions.set(m.action_id, cur);
      state.actions = new Map(state.actions);
      break;
    }
    case 'action_result': {
      const cur = state.actions.get(m.action_id) ?? { progress: 100, status: 'pending' as const, consoleLines: [] };
      cur.status = m.ok ? 'ok' : 'error';
      cur.error = m.error;
      cur.result = m.data;
      cur.progress = 100;
      state.actions.set(m.action_id, cur);
      state.actions = new Map(state.actions);
      break;
    }
    case 'bulk_started': {
      // Only consume events for the run currently in flight in the UI.
      if (!state.bulk || state.bulk.bulk_id !== m.bulk_id) break;
      const next = new Map<number, BulkServerEntry>();
      for (const s of m.servers) {
        next.set(s.Server_ID, {
          Server_ID: s.Server_ID,
          Server_Name: s.Server_Name,
          Server_Machine: s.Server_Machine,
          status: 'queued',
          progress: 0,
          consoleLines: [],
        });
      }
      state.bulk = { ...state.bulk, servers: next };
      break;
    }
    case 'bulk_server_progress': {
      if (!state.bulk || state.bulk.bulk_id !== m.bulk_id) break;
      const cur = state.bulk.servers.get(m.Server_ID);
      if (!cur) break;
      const next: BulkServerEntry = {
        ...cur,
        status: m.status,
        progress: m.progress ?? cur.progress,
        message: m.message ?? cur.message,
        error: m.error ?? cur.error,
        consoleLines: m.console
          ? [...cur.consoleLines, m.console].slice(-200)
          : cur.consoleLines,
      };
      const servers = new Map(state.bulk.servers);
      servers.set(m.Server_ID, next);
      state.bulk = { ...state.bulk, servers };
      break;
    }
    case 'bulk_done': {
      if (!state.bulk || state.bulk.bulk_id !== m.bulk_id) break;
      state.bulk = {
        ...state.bulk,
        done: true,
        totals: {
          total: m.total,
          succeeded: m.succeeded,
          failed: m.failed,
          skipped: m.skipped,
          cancelled: m.cancelled,
        },
      };
      const cmd = state.bulk.command;
      const hadErrors = m.failed > 0;
      pushNotification({
        level: hadErrors ? 'warn' : 'success',
        title: `Bulk ${cmd} ${hadErrors ? 'completed with errors' : 'complete'}`,
        message: `${m.succeeded}/${m.total} ok${m.failed ? ` · ${m.failed} failed` : ''}${m.skipped ? ` · ${m.skipped} skipped` : ''}`,
        link: '/bulk',
      });
      break;
    }
  }
  emit();
}

// -------------------------------------------------------------------
// Public API
// -------------------------------------------------------------------

export function startWebSocket(): void {
  if (ws) return;
  connect();
}

export function subscribeTopics(topics: string[]): void {
  send({ type: 'subscribe', topics });
}
export function unsubscribeTopics(topics: string[]): void {
  send({ type: 'unsubscribe', topics });
}

export function dispatchAction(Server_ID: number, command: NodeCommand, args?: Record<string, unknown>): string {
  const action_id = `${command}-${Server_ID}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  send({ type: 'action', action_id, Server_ID, command, args });
  state.actions.set(action_id, { progress: 0, status: 'pending', consoleLines: [] });
  state.actions = new Map(state.actions);
  emit();
  return action_id;
}

export function dispatchBulk(
  command: BrowserBulkActionMsg['command'],
  target: BrowserBulkActionMsg['target'],
  args?: Record<string, unknown>,
): string {
  const bulk_id = `bulk-${command}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  state.bulk = {
    bulk_id,
    command,
    target,
    startedAt: Date.now(),
    servers: new Map(),
    totals: null,
    done: false,
  };
  emit();
  send({ type: 'bulk_action', bulk_id, command, target, args });
  return bulk_id;
}

export function cancelBulk(bulk_id: string): void {
  send({ type: 'bulk_cancel', bulk_id });
}

export function clearBulk(): void {
  if (state.bulk?.done) {
    state.bulk = null;
    emit();
  }
}

/** React hook: subscribe to a topic for as long as the component is mounted. */
export function useTopic(topic: string | null): void {
  const ref = useRef<string | null>(null);
  useEffect(() => {
    if (topic === ref.current) return;
    if (ref.current) unsubscribeTopics([ref.current]);
    if (topic) subscribeTopics([topic]);
    ref.current = topic;
    return () => {
      if (ref.current) {
        unsubscribeTopics([ref.current]);
        ref.current = null;
      }
    };
  }, [topic]);
}
